import { Injectable, computed, signal } from '@angular/core';
import type { GroupContext } from '../models/auth.model';

export type RestaurantViewMode = 'group' | 'outlet';

const STORAGE_KEY = 'menuvia_restaurant_context';

@Injectable({ providedIn: 'root' })
export class RestaurantContextService {
  private readonly groupContextSignal = signal<GroupContext | null>(null);
  /** `null` = vue groupe ; sinon id de l'outlet actif. */
  private readonly activeOutletIdSignal = signal<number | null>(this.restoreOutletId());

  readonly groupContext = this.groupContextSignal.asReadonly();
  readonly activeOutletId = this.activeOutletIdSignal.asReadonly();

  readonly isGroupEnabled = computed(() => this.groupContextSignal()?.enabled === true);

  readonly viewMode = computed((): RestaurantViewMode =>
    this.activeOutletIdSignal() == null ? 'group' : 'outlet',
  );

  readonly contextRestaurantId = computed(() => {
    const outletId = this.activeOutletIdSignal();
    if (outletId != null) {
      return outletId;
    }
    const ctx = this.groupContextSignal();
    return ctx?.masterRestaurantId ?? ctx?.homeRestaurantId ?? null;
  });

  setGroupContext(ctx: GroupContext | null | undefined): void {
    this.groupContextSignal.set(ctx ?? null);
    if (!ctx?.enabled) {
      this.setActiveOutlet(null);
      return;
    }
    const activeId = this.activeOutletIdSignal();
    if (
      !ctx.canViewGroup &&
      (activeId == null || !ctx.outlets.some((o) => o.id === activeId))
    ) {
      this.setActiveOutlet(ctx.outlets[0]?.id ?? null);
    } else if (activeId != null && !ctx.outlets.some((o) => o.id === activeId)) {
      this.setActiveOutlet(null);
    }
  }

  setActiveOutlet(outletId: number | null): void {
    this.activeOutletIdSignal.set(outletId);
    if (outletId == null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(outletId));
    }
  }

  selectGroupView(): void {
    if (this.groupContextSignal()?.canViewGroup) {
      this.setActiveOutlet(null);
    }
  }

  selectOutlet(outletId: number): void {
    this.setActiveOutlet(outletId);
  }

  outletHeaderValue(): string | null {
    const outletId = this.activeOutletIdSignal();
    if (outletId == null || !this.isGroupEnabled()) {
      return null;
    }
    return String(outletId);
  }

  private restoreOutletId(): number | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
}
